import React, { useState, useRef, useEffect } from 'react';
import { useStateValue } from './StateProvider';
import Title from './Title';
import Select from './Components/select.js';
import configData from './Config';
import axios from 'axios';
import { getOrderCost, getOrderPrice } from './Utils/items.js';
import moment from 'moment';
import { findNearestExchangeRate } from './Utils/currency.js';

import './POS.css';

export const tableStatus = [
    { key: 'free', name: 'Free', color: '#4CAF50' },
    { key: 'occupied', name: 'Occupied', color: '#F44336' },
    { key: 'notavailable', name: 'Not Available', color: '#9E9E9E' },
    { key: 'reserved', name: 'Reserved', color: '#FFC107' },
    { key: 'cleaning', name: 'Cleaning', color: '#03A9F4' },
];

const navItems = [
    { key: 'order', icon: '🛒', label: 'Order' },
    { key: 'tables', icon: '🍽️', label: 'Tables' },
    { key: 'checkout', icon: '💳', label: 'Checkout' },
    { key: 'kds', icon: '🍳', label: 'Kitchen Display' },
    { key: 'register', icon: '💰', label: 'Register' },
];

const kitchenStatus = [
    { id: 'new', name: 'New' },
    { id: 'inprogress', name: 'In Progress' },
    { id: 'completed', name: 'Completed' },
];

function POSNavBar({ module, setModule }) {
    return (
        <div className='posnavbar'>
            {navItems.map(item => (
                <div
                    key={item.key}
                    onClick={() => setModule(item.key)}
                    className={module == item.key ? 'posnavbar_item posnavbar_itemactive' : 'posnavbar_item'}
                >
                    <div className='posnavbar_itemicon'>{item.icon}</div>
                    <div className='posnavbar_itemlabel'>{item.label}</div>
                </div>
            ))}
        </div>
    );
}

function POS({ state, setState }) {
    const [{ project }, dispatch] = useStateValue();

    const [postype, setPostype] = useState('');
    const [posmargin, setPosmargin] = useState(0);

    const [order, setOrder] = useState([]);
    const [tables, setTables] = useState([]);
    const [tablesorders, setTablesorders] = useState([]);
    const [kitchenorders, setKitchenorders] = useState([]);
    const [register, setRegister] = useState([]);
    const [checkout, setCheckout] = useState({});
    const [module, setModule] = useState('order');

    useEffect(() => {
        setPostype(project.pos.type)
        setPosmargin(project.pos.margin)
        setTables(project.pos.tables)
    }, [project.pos]);

    function renderModule() {
        switch (module) {
            case 'order':
                return <Order order={order} setOrder={setOrder} />;
            case 'tables':
                return <Tables tables={tables} setTables={setTables} />;
            case 'checkout':
                return <Checkout order={order} setOrder={setOrder} />;
            case 'kds':
                return <KitchenDisplay />;
            case 'register':
                return <Register />;
            default:
                return null;
        }
    };

    function Order() {
        const [itemid, setItemid] = useState('');
        const [modifierid, setModifierid] = useState('');

        const [categories, setCategories] = useState(project.itemscategories);
        const [items, setItems] = useState([]);

        const orderprice = getOrderPrice(
            order,
            project.exchangerates,
            project.basecurrency
        )

        const basecurrencysymbol = project.currencies.find(currency => currency.id == project.basecurrency)?.symbol || '';

        const printRef = useRef();

        const handleClear = () => {
            setOrder([])
        }

        const handleRemoveItem = (id) => {
            setOrder(prev => {
                return prev
                    .map(p => {
                        if (p.accountid === id) {
                            const qty = p.itemquantity - 1;
                            return qty > 0 ? { ...p, itemquantity: qty } : null;
                        }
                        return p;
                    })
                    .filter(Boolean);
            });
        };

        const totalsByCurrency = [];

        order.forEach(i => {
            const currency = i.itemcurrency || 'Unknown';
            const subtotal = i.itemprice * i.itemquantity;
            const existing = totalsByCurrency.find(t => t.currency === currency);

            if (existing) {
                existing.total += subtotal;
            } else {
                totalsByCurrency.push({ currency: currency, total: subtotal });
            }
        });

        function handleCategory(itemcategory) {
            let filtered = project.items.filter(item => item.categoryid == itemcategory && item.posvisible)
            setItems(filtered)
        }

        const handleItem = (itemid) => {
            const item = project.items?.find(i => i.id === itemid);
            if (!item) return;

            let price = parseFloat(item.pricing[0]?.priceamount) || 0;
            const currencyid = item.pricing[0]?.pricecurrencyid || '';
            const currencysymbol = project?.currencies?.find(c => c.id == currencyid)?.symbol || '';

            const taxid = item.taxid;
            const tax = project.taxes.find(t => t.id == taxid);

            let taxItem = null;

            if (tax) {
                tax.items.forEach((taxitem) => {
                    let taxprice = 0;
                    if (taxitem.type === "percentage") {
                        if (taxitem.mode === "exclusive") {
                            taxprice = price * (taxitem.rate / 100);
                        }
                        if (taxitem.mode === "inclusive") {
                            const basePrice = price / (1 + taxitem.rate / 100);
                            taxprice = price - basePrice;
                            price = basePrice;
                        }
                    }
                    if (taxitem.type === "fixed") {
                        if (taxitem.mode === "exclusive") {
                            taxprice = taxitem.rate;
                        }
                        if (taxitem.mode === "inclusive") {
                            taxprice = taxitem.rate;
                            price = price - taxprice;
                        }
                    }

                    if (taxprice > 0) {
                        taxItem = {
                            accounttype: taxitem.accounttype,
                            accountid: taxitem.accountid,
                            currencyid: taxitem.currencyid,
                            ts: Math.floor(Date.now() / 1000),
                            itemname: tax.name,
                            itemprice: taxprice,
                            itemquantity: 1,
                            itemcurrency: currencysymbol,
                            itemunityofmeasurement: 'unit',
                        };
                    }
                });
            }

            const itemExists = order.some(i => i.accountid == itemid && i.accounttype === 'items');

            if (itemExists) {
                setOrder(prevOrder => prevOrder.map(line => {
                    if (line.accountid == itemid && line.accounttype === 'items') {
                        return { ...line, itemquantity: line.itemquantity + 1 };
                    }

                    if (taxItem && line.accountid == taxItem.accountid && line.accounttype === taxItem.accounttype) {
                        return { ...line, itemprice: line.itemprice + taxItem.itemprice };
                    }

                    return line;
                }));
            } else {
                const newItem = {
                    accounttype: 'items',
                    accountid: item.id,
                    currencyid: currencyid,
                    ts: Math.floor(Date.now() / 1000),
                    itemname: item.name,
                    itemprice: price,
                    itemquantity: 1,
                    itemcurrency: currencysymbol,
                    itemunityofmeasurement: item.unitofmeasurement,
                };

                setOrder(prevOrder => {
                    let baseOrder = [...prevOrder, newItem];

                    if (taxItem) {
                        const taxInOrder = baseOrder.some(i => i.accountid == taxItem.accountid && i.accounttype === taxItem.accounttype);

                        if (taxInOrder) {
                            return baseOrder.map(line =>
                                (line.accountid == taxItem.accountid && line.accounttype === taxItem.accounttype)
                                    ? { ...line, itemprice: line.itemprice + taxItem.itemprice }
                                    : line
                            );
                        } else {
                            return [...baseOrder, taxItem];
                        }
                    }
                    return baseOrder;
                });
            }
        };

        const handleAddModifier = (modifierid) => {
            const modifier = project?.itemsmodifiers?.find(i => i.id === modifierid);
            if (!modifier) return;
            const price = parseFloat(modifier.price) || 0;
            const currencyid = modifier.currencyid
            const currencysymbol = project?.currencies?.find(c => c.id == currencyid)?.symbol || '';

            setOrder([
                ...order,
                {
                    accounttype: 'items',
                    accountid: modifier.id,
                    itemname: modifier.name,
                    itemprice: price,
                    itemquantity: 1,
                    itemcurrency: currencysymbol,
                    itemunityofmeasurement: 'unit'
                }
            ]);
        };

        const handleCheckout = () => {
            setModule('checkout')
        }

        return (
            <div className='pos_order'>
                <div className='buttonscontainer'>
                    <div className="buttonsection">
                        <Select data={project.items.filter(item => item.posvisible)} itemid={itemid} setItemid={setItemid} />
                    </div>
                    <div className="buttonsection">
                        <button className='button' onClick={() => handleItem(itemid)}>Add</button>
                    </div>
                    <div className="buttonsection">
                        <button className='button' onClick={() => handleClear()}>Clear</button>
                    </div>
                    <div className="buttonsection">
                        <button className='button' onClick={() => handleCheckout()}>Checkout</button>
                    </div>
                </div>
                <div className='buttonscontainer'>
                    <div className="buttonsection">
                        <Select data={project.itemsmodifiers} itemid={modifierid} setItemid={setModifierid} />
                    </div>
                    <div className="buttonsection">
                        <button className='button' onClick={() => handleAddModifier(modifierid)}>Add</button>
                    </div>
                </div>

                <div className='pos_orders'>
                    <div className="pos_order_menu">
                        {
                            categories.map((category, index) => {
                                return (
                                    <div key={'category' + index} className="pos_order_menuitem" onClick={() => handleCategory(category.id)}>
                                        {category.image && (
                                            <img src={category.image} alt={category.name} />
                                        )}
                                        <span>{category.name}</span>
                                    </div>
                                )
                            })
                        }
                        {
                            items.map((item, index) => {
                                return (
                                    <div key={'item' + index} className="pos_order_menuitem" onClick={() => handleItem(item.id)}>
                                        {item.image && (
                                            <img src={item.image} alt={item.name} />
                                        )}
                                        <span>{item.name}</span>
                                    </div>
                                )
                            })
                        }
                    </div>

                    <div className='pos_order_summary' ref={printRef}>
                        {project.projectdetailsimage && (
                            <img
                                className="pos_order_projectdetailsimage"
                                src={project.projectdetailsimage}
                                alt="Project Details Image"
                            />
                        )}

                        <div className='pos_invoice_projectname'>{project.projectdetails}</div>
                        <div className='modal_rowsection'>{moment().format('D MMMM YYYY HH:mm')}</div>
                        {
                            order.length != 0 &&
                            <table className='pos_invoice_table'>
                                <tbody>
                                    {
                                        order
                                            .slice()
                                            .sort((a, b) => {
                                                if (a.accounttype === 'items' && b.accounttype !== 'items') return -1;
                                                if (a.accounttype !== 'items' && b.accounttype === 'items') return 1;
                                                return 0;
                                            })
                                            .map((ord, index) => {
                                                return (
                                                    <tr key={'order' + index} onClick={() => handleRemoveItem(ord.accountid)}>
                                                        <td>{ord.itemquantity}</td>
                                                        <td>{ord.itemname}</td>
                                                        <td>
                                                            {ord.itemcurrency + ' ' + (ord.itemprice * ord.itemquantity).toLocaleString('en')}
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                    }
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan={2}>Total</td>
                                        <td>{basecurrencysymbol + ' ' + orderprice.toLocaleString('en')}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        }
                        <h4>You have been served by {state.user.username}</h4>
                        <h4>Thank you</h4>
                        <h4>Powered by {configData.APPNAME}</h4>
                    </div>
                </div>
            </div>
        );
    }

    function Tables({ tables, setTables }) {

        const closeButton = () => {
            setState(state => ({ ...state, modalopen: false, modalcontent: null, modaltype: null, modaltitle: null }));
        }

        const viewOptions = (table) => {
            setState(state => ({ ...state, modalopen: true, modalcontent: <ViewOptions table={table} />, modaltype: 'small', modaltitle: 'Table' }));
        }

        const handleOrder = (table) => {
            setModule('order')
            setState(state => ({ ...state, modalopen: false, modalcontent: null, modaltype: null, modaltitle: null }));
        }

        const handleCheckout = () => {
            setModule('checkout')
            setState(state => ({ ...state, modalopen: false, modalcontent: null, modaltype: null, modaltitle: null }));
        }

        function ViewOptions({ table }) {
            const [itemid, setItemid] = useState('');

            const handleAddOrder = (table, setTable, itemid) => {
                const item = project?.items?.find(i => i.id === itemid);
                if (!item) return;

                const price = parseFloat(item.pricing[0]?.priceamount) || 0;
                const currencyid = item.pricing[0]?.pricecurrencyid;
                const currencysymbol = project?.currencies?.find(c => c.id === currencyid)?.symbol;

                setTables(prevTables =>
                    prevTables.map(t => {
                        if (t.id !== table.id) return t;

                        const existing = t.order?.find(o => o.id === item.id);
                        let updatedOrder;

                        if (existing) {
                            updatedOrder = t.order.map(o =>
                                o.id === item.id ? { ...o, itemquantity: o.itemquantity + 1 } : o
                            );
                        } else {
                            const newItem = {
                                id: item.id,
                                name: item.name,
                                itemprice: price,
                                itemquantity: 1,
                                itemcurrency: currencysymbol,
                                itemunityofmeasurement: item.unitofmeasurement
                            };
                            updatedOrder = [...(t.order || []), newItem];
                        }

                        return { ...t, order: updatedOrder };
                    })
                );
            };

            return (
                <div className='modal_body'>
                    <div className='modal_printable'>
                        <div className='modal_row'>
                            <div className='modal_rowsection'>
                                <div className='modal_button' onClick={() => handleOrder()}>Order</div>
                            </div>
                            <div className='modal_rowsection'>
                                <div className='modal_button' onClick={() => handleCheckout()}>Checkout</div>
                            </div>
                            <div className='modal_rowsection'>
                                <div className='modal_button'>Status</div>
                            </div>
                        </div>
                    </div>
                    <div className='modal_actions'>
                        <div className='modal_buttons'>
                            <div className='modal_buttoncontainer'>
                                <button className="modal_button" onClick={closeButton}>Cancel</button>
                            </div>
                        </div>
                    </div>
                </div>
            )
        }

        function Table({ table }) {
            const [itemid, setItemid] = useState('');

            let currencySymbol = project.currencies.find(curr => curr.id == project.basecurrency)?.symbol;

            let statusName = tableStatus.find(s => s.key === table.status)?.name

            const ordercost = getOrderCost(
                tablesorders.filter(order => order.tableid == table.id),
                project.purchaseinvoices,
                project.productionorders,
                project.exchangerates,
                project.basecurrency
            )

            const orderprice = getOrderPrice(
                tablesorders.filter(order => order.tableid == table.id),
                project.exchangerates,
                project.basecurrency
            )

            function TableMenu({ table, setTables }) {

                function Order({ table, setTables }) {
                    const [itemid, setItemid] = useState('');

                    const handleAddOrder = (table, setTable, itemid) => {
                        const item = project?.items?.find(i => i.id === itemid);
                        if (!item) return;

                        const price = parseFloat(item.pricing[0]?.priceamount) || 0;
                        const currencyid = item.pricing[0]?.pricecurrencyid;
                        const currencysymbol = project?.currencies?.find(c => c.id === currencyid)?.symbol;

                        setTables(prevTables =>
                            prevTables.map(t => {
                                if (t.id !== table.id) return t;

                                const existing = t.order?.find(o => o.id === item.id);
                                let updatedOrder;

                                if (existing) {
                                    updatedOrder = t.order.map(o =>
                                        o.id === item.id ? { ...o, itemquantity: o.itemquantity + 1 } : o
                                    );
                                } else {
                                    const newItem = {
                                        id: item.id,
                                        name: item.name,
                                        itemprice: price,
                                        itemquantity: 1,
                                        itemcurrency: currencysymbol,
                                        itemunityofmeasurement: item.unitofmeasurement
                                    };
                                    updatedOrder = [...(t.order || []), newItem];
                                }

                                return { ...t, order: updatedOrder };
                            })
                        );
                    };

                    return (
                        <div className='pos_tablemenumodal'>
                            <div className='modal_rowsection'>
                                <Select data={project.items.filter(item => item.posvisible)} itemid={itemid} setItemid={setItemid} />
                            </div>
                            <div className='modal_rowsection'>
                                <button className="button" onClick={() => handleAddOrder(table, setTables, itemid)} >Add</button>
                            </div>
                        </div>
                    )
                }

                function Status({ table, setTables }) {
                    const handleStatusChange = (table, statusKey) => {
                        setTables(prevTables =>
                            prevTables.map(t =>
                                t.id === table.id ? { ...t, status: statusKey } : t
                            )
                        );
                    };

                    return (
                        <div className='pos_tablemenumodal'>
                            <div className='modal_rowsection'>
                                {tableStatus.map(status => (
                                    <button
                                        key={status.key}
                                        className={`modal_button ${table.status === status.key ? 'active' : ''}`}
                                        onClick={() => handleStatusChange(table, status.key)}
                                    >
                                        {status.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    );
                }

                const handleCheckout = (table) => {
                    setOrder([])
                    setOrder(table.order)
                    setModule('checkout')
                }

                const handleClear = (table) => {

                }

                return (
                    <div className='pos_tablemenumodal'>
                        <div onClick={() => handleOrder()}>Add order</div>
                        <div onClick={() => handleCheckout(table)}>Checkout</div>
                        <div onClick={() => handleClear(table)}>Clear</div>
                    </div>
                )
            }

            return (
                <div
                    className='pos_tables_table'
                    onClick={() => viewOptions(table)}
                >
                    <div className='pos_tablename'>{table.name}</div>
                    <div className='pos_tablestatus'>{statusName}</div>

                    <div className='pos_tableorders'>
                        <ul>
                            {
                                tablesorders
                                    .filter(order => order.tableid == table.id)
                                    .map((item, i) => (
                                        <li key={'order' + i}>{item.itemquantity} x {item.name}</li>
                                    ))
                            }
                        </ul>
                        <div>
                            Cost: {currencySymbol + ' ' + ordercost.toLocaleString('en')}
                        </div>
                        <div>
                            Price:{currencySymbol + ' ' + orderprice.toLocaleString('en')}
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="pos_tables">
                <div className="buttonscontainer">
                    <button className='button'>Add</button>
                </div>
                <div className="pos_tables_container">
                    {
                        tables.map((table, index) => (
                            <Table
                                key={'table' + index}
                                table={table}
                            />
                        ))
                    }
                </div>
            </div>
        );
    }

    function Checkout({ order }) {
        const [orderamount, setOrderamount] = useState(0);
        const [ordercurrencyid, setOrdercurrencyid] = useState(project.basecurrency);

        const [paymentamount, setPaymentamount] = useState(0);
        const [paymentcurrencyid, setPaymentcurrencyid] = useState(project.basecurrency);

        const [payments, setPayments] = useState([]);

        const [paymentstotal, setPaymentstotal] = useState(0);
        const [change, setChange] = useState(0);

        useEffect(() => {
            let price = getOrderPrice(
                order,
                project.exchangerates,
                project.basecurrency
            )
            setOrderamount(price || 0)
        }, [project.items, order, project.exchangerates, project.basecurrency]);

        useEffect(() => {

            let total = 0;

            payments.forEach(payment => {

                let amount = Number(payment.amount);

                if (payment.currencyid !== project.basecurrency) {

                    const NearestExchangeRate = findNearestExchangeRate(
                        project.exchangerates,
                        Math.floor(Date.now() / 1000),
                        payment.currencyid,
                        project.basecurrency
                    );

                    const exchangerate = NearestExchangeRate?.exchangerate || 1;

                    amount = amount * exchangerate;
                }

                total += amount;
            });

            setPaymentstotal(total);

            if (total) setChange(Math.abs(orderamount - total));
            else setChange(0);

        }, [payments, project.exchangerates, project.basecurrency, orderamount]);

        const handleConfirmPayment = () => {
            if (orderamount == 0) return
            if (paymentstotal == 0) return

            const lastId = register.length > 0 ? Number(register[register.length - 1].id) : 0;

            const newinvoice = {
                type: "invoice",
                id: lastId + 1,
                ts: Math.floor(Date.now() / 1000),
                name: "Payment",
                subaccountid: "JNIxcNPD",
                amount: orderamount,
                currencyid: ordercurrencyid,
                items: order
            };

            const newreceipt = {
                type: "receipt",
                id: lastId + 2,
                ts: Math.floor(Date.now() / 1000),
                name: "Payment",
                fromtype: "clients",
                from: "JEMShQiL",
                totype: "cashaccounts",
                to: "7kqno3fR",
                subaccountid: "JNIxcNPD",
                amount: paymentstotal,
                currencyid: paymentcurrencyid
            };

            const updatedRegister = [...register, newinvoice, newreceipt];

            setRegister(updatedRegister);

            localStorage.setItem("register", JSON.stringify(updatedRegister));

            setOrder([]);
            setModule('order');
        };

        function NumberPad() {
            const buttons = [
                1, 2, 3,
                4, 5, 6,
                7, 8, 9,
                'C', 0, 'OK',
                '000',
            ];

            function handleClick(value) {
                if (value === 'C') {
                    setPaymentamount(0);
                    setPaymentcurrencyid(project.basecurrency);
                    setPayments([]);
                    return;
                }

                if (value === 'OK') {
                    const lastId = payments.length > 0 ? Number(payments[payments.length - 1].id) : 0;

                    setPayments(prev => [...prev, { id: lastId + 1, amount: paymentamount, currencyid: paymentcurrencyid }]);
                    setPaymentamount(0)
                    return;
                }


                setPaymentamount(prev =>
                    prev === 0
                        ? Number(value)
                        : Number(String(prev) + String(value))
                );
            }

            return (
                <div className="pos_numpad">
                    {buttons.map((btn, i) => (
                        <div
                            key={i}
                            className="pos_numpad_btn"
                            onClick={() => handleClick(btn)}
                        >
                            {btn}
                        </div>
                    ))}
                </div>
            );
        }

        const handlePaymentcurrencyid = (id) => {
            setPaymentcurrencyid(id)
        }

        const orderCurrencySymbol = project.currencies.find(curr => curr.id == ordercurrencyid)?.symbol || '';
        const paymentCurrencySymbol = project.currencies.find(curr => curr.id == paymentcurrencyid)?.symbol || '';

        const handleRemovePayment = (id) => {
            setPayments(prev => prev.filter(payment => payment.id !== id));
        };

        return (
            <div className="pos_checkout">
                <div className='buttonscontainer'>
                    <div className='buttonsection'>
                        <button
                            onClick={() => handleConfirmPayment()}
                            className='button'
                        >
                            Confirm Payment
                        </button>
                    </div>
                </div>

                <div className='modal_row'>
                    <div className='modal_rowsection'>
                        <div className='pos_checkout_amount'>{orderamount.toLocaleString('en') + ' ' + orderCurrencySymbol}</div>
                    </div>
                </div>

                <div className="pos_checkouts">
                    <div className="pos_checkout_numpad">
                        <div className='modal_row'>
                            <div className='modal_rowsection'>
                                <NumberPad />
                            </div>
                            <div className='modal_rowsection'>
                                {project.currencies.map((currency, index) => {
                                    return (
                                        <button key={'button' + index} className='button' onClick={() => handlePaymentcurrencyid(currency.id)}>{currency.name}</button>
                                    )
                                })}
                            </div>
                        </div>
                    </div>

                    <div className="pos_checkout_summary">
                        <div className='modal_row'>
                            <div className='modal_rowsection'>
                                <div className='modal_label'>
                                    <div className='pos_checkout_paymenttotal'>{paymentamount.toLocaleString('en') + ' ' + paymentCurrencySymbol}</div>

                                    {
                                        payments.map((payment, index) => {
                                            const currencySymbol = project.currencies.find(curr => curr.id == payment.currencyid)?.symbol || '';
                                            return (
                                                <div key={'payment' + index} className='modal_label' onClick={() => handleRemovePayment(payment.id)}>{payment.amount.toLocaleString('en') + ' ' + currencySymbol}</div>
                                            )
                                        })
                                    }
                                    <div className='modal_label'>Total: {paymentstotal.toLocaleString('en')}</div>
                                    {change > 0 && <div className='modal_label'>Remaining: {change.toLocaleString('en')}</div>}
                                    {change < 0 && <div className='modal_label'>Return: {change.toLocaleString('en')}</div>}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    function KitchenDisplay() {
        const [orders, setOrders] = useState([]);

        useEffect(() => {

        }, []);

        const updateStatus = (id, newStatus) => {
            setOrders(prev =>
                prev.map(order =>
                    order.id === id ? { ...order, status: newStatus } : order
                )
            );
        };

        return (
            <div style={{ display: 'flex', gap: '20px', padding: '20px' }}>
                {kitchenStatus.map(status => (
                    <div key={status.name} style={{ flex: 1, border: '1px solid #ccc', borderRadius: '8px', padding: '10px' }}>
                        <h2>{status.name}</h2>
                        {orders
                            .filter(order => order.status === status.id)
                            .map(order => (
                                <div key={order.id} style={{ borderBottom: '1px solid #eee', marginBottom: '10px', paddingBottom: '10px' }}>
                                    <strong>Table {order.table}</strong>
                                    <ul>
                                        {order.items.map((item, i) => (
                                            <li key={i}>{item}</li>
                                        ))}
                                    </ul>
                                    {status.id !== 'Completed' && (
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            {status !== 'In Progress' && (
                                                <button onClick={() => updateStatus(order.id, 'In Progress')}>Start</button>
                                            )}
                                            <button onClick={() => updateStatus(order.id, 'Completed')}>Complete</button>
                                        </div>
                                    )}
                                </div>
                            ))}
                    </div>
                ))}
            </div>
        );
    }


    function Register() {
        const [register, setRegister] = useState([]);

        let total = 0;

        register.forEach(payment => {
            total += Number(payment.amount);
        });

        useEffect(() => {
            const saved = localStorage.getItem("register");
            if (saved) {
                setRegister(JSON.parse(saved));
            }
        }, []);

        const handleCloseRegister = () => {
            console.log(register)

            let allItems = [];
            let totalReceipt = 0;
            let receiptCurrency = null;

            register.forEach(entry => {
                if (entry.type === "order") {
                    if (entry.items) {
                        allItems = [...allItems, ...entry.items];
                    }
                }

                if (entry.type === "receipts") {
                    totalReceipt += Number(entry.amount);
                    receiptCurrency = entry.currencyid;
                }
            });

            const mergedSalesInvoices = {
                type: "salesinvoices",
                ts: Math.floor(Date.now() / 1000),
                name: "POS - Invoices",
                subaccountid: "JNIxcNPD",
                items: allItems
            };

            const mergedTransactions = {
                type: "transactions",
                id: 2,
                ts: Math.floor(Date.now() / 1000),
                name: "POS - Receipts",
                fromtype: "clients",
                from: "JEMShQiL",
                totype: "cashaccounts",
                to: "7kqno3fR",
                subaccountid: "JNIxcNPD",
                amount: total,
                currencyid: receiptCurrency
            };

            console.log(mergedSalesInvoices)
            console.log(mergedTransactions)
        }

        return (
            <div>
                <div className='buttonscontainer'>
                    <div className='buttonsection'>
                        <button
                            className='button'
                            onClick={handleCloseRegister}
                        >
                            Close Register
                        </button>
                    </div>
                </div>
                <div className='modal_row'>
                    <div className='modal_rowsection'>
                        <div className='modal_label'>{total.toLocaleString('en')}</div>
                    </div>
                </div>
                <div className='modal_row'>
                    <div className='modal_rowsection'>
                        <table className='modal_table'>
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Date</th>
                                    <th>Type</th>
                                    <th>Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    register.sort((a, b) => b.id - a.id).map((registeritem, index) => {
                                        const date = new Date(parseInt(registeritem.ts) * 1000).toLocaleString();
                                        const currencySymbol = project.currencies.find(curr => curr.id == registeritem.currencyid)?.symbol || '';
                                        return (
                                            <tr key={"registeritem" + index}>
                                                <td>{registeritem.id}</td>
                                                <td>{date}</td>
                                                <td>{registeritem.type}</td>
                                                <td>{currencySymbol + ' ' + registeritem.amount.toLocaleString('en')}</td>
                                            </tr>
                                        );
                                    })
                                }
                            </tbody>
                        </table>
                    </div>
                </div>
            </div >
        );
    }

    return (
        <div className='pos'>
            <Title text='POS' />
            <div className='buttonscontainer'>
                <div className='buttonsection'>
                    <button className="button" onClick={() => document.documentElement.requestFullscreen()}>
                        Go Full Screen
                    </button>
                </div>
            </div>

            <div>
                <h2>{navItems.find(i => i.key === module)?.label}</h2>
                {renderModule()}
            </div>
            <POSNavBar module={module} setModule={setModule} />
        </div>
    );
}

export default POS;